import { Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Text } from '@/ui-kit'
import clsx from 'clsx'
import { useMemo, useRef, useState } from 'react'

export type FormSelectOption = { value: string; label: string }

type FormSelectProps = {
  className?: string
  value: string | undefined
  onChange: (value: string) => void
  label: string
  options: readonly FormSelectOption[]
  placeholder: string
  errorMessage?: string
  searchable?: boolean
}

const SEARCH_MIN_OPTIONS = 10
const VISIBLE_OPTIONS_LIMIT = 15

export const FormSelect = ({
  className,
  value,
  onChange,
  label,
  options,
  placeholder,
  errorMessage,
  searchable = false
}: FormSelectProps) => {
  const isError = !!errorMessage
  const [searchQuery, setSearchQuery] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)
  const valueLabel = useMemo(() => {
    return options.find((opt) => opt.value === value)?.label || placeholder
  }, [options, value])

  const { filteredOptions, hasMoreMatches } = useMemo(() => {
    if (!searchable) return { filteredOptions: options, hasMoreMatches: false }
    if (!searchQuery.trim()) {
      const list = options.slice(0, VISIBLE_OPTIONS_LIMIT)
      return { filteredOptions: list, hasMoreMatches: options.length > VISIBLE_OPTIONS_LIMIT }
    }
    const q = searchQuery.trim().toLowerCase()
    const filtered = options.filter((opt) => opt.label.toLowerCase().includes(q))
    const visible = filtered.slice(0, VISIBLE_OPTIONS_LIMIT)
    return { filteredOptions: visible, hasMoreMatches: filtered.length > VISIBLE_OPTIONS_LIMIT }
  }, [options, searchable, searchQuery])

  const showSearch = searchable && options.length >= SEARCH_MIN_OPTIONS

  /** After Radix positions content it focuses the selected item; refocus search so Android keeps the keyboard. */
  const focusSearchInputDeferred = () => {
    const run = () => searchInputRef.current?.focus({ preventScroll: true })
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        run()
        setTimeout(run, 0)
      })
    })
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSearchQuery('')
      return
    }
    if (showSearch) {
      focusSearchInputDeferred()
    }
  }

  return (
    <div className={clsx('flex flex-col gap-1', className)}>
      <Select value={value || undefined} onValueChange={onChange} onOpenChange={handleOpenChange}>
        <SelectTrigger
          label={label}
          className={clsx('flex h-[56px] w-full flex-col items-start gap-0!', { 'border-red-500': isError })}
        >
          <SelectValue placeholder={placeholder}>{valueLabel}</SelectValue>
        </SelectTrigger>
        <SelectContent className={clsx({ 'pt-2': showSearch })}>
          {showSearch && (
            <div className="backgroundColorBlackToWhite sticky top-0 z-10 border-b border-solid border-gray-100 p-1">
              <Input
                ref={searchInputRef}
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search..."
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>
          )}
          <div
            className="min-h-0 flex-1"
            onPointerMoveCapture={(e) => {
              if (searchInputRef.current && document.activeElement === searchInputRef.current) {
                e.stopPropagation()
              }
            }}
          >
            {filteredOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </div>
          {showSearch && hasMoreMatches && (
            <div className="text-gray-80 text-body3-regular border-t border-solid border-gray-100 px-2 py-2 text-center">
              Refine your query to see more options
            </div>
          )}
          {showSearch && filteredOptions.length === 0 && (
            <div className="text-gray-80 text-body3-regular py-4 text-center">No matches</div>
          )}
        </SelectContent>
      </Select>
      {errorMessage && (
        <Text type="body3-regular" className="text-red-500">
          {errorMessage}
        </Text>
      )}
    </div>
  )
}
